import type { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import styles from "../LegalIdVerification.module.scss";
import type { ConsumerConfig } from "../../../consumers";
import { PartnerFooter } from "./PartnerFooter";

interface WhiteLabelIntroScreenProps {
  introCopy: NonNullable<ConsumerConfig["legalIdIntro"]>;
  /** Already tracked by IntroScreen, which owns the Legal ID intro analytics. */
  onOpenCamera: () => void;
  onFallback: () => void;
  fallbackAvailable: boolean;
  showPartnerFooter: boolean;
  /** Extra actions under Open camera, supplied by the host (e.g. the mock's Skip Camera). */
  children?: ReactNode;
}

/**
 * Legal ID landing for white-label consumers that supply their own copy
 * (consumerConfig.legalIdIntro): consumer heading and supporting text, the landing
 * illustration, the consent line, and the host-owned fallback link.
 */
export function WhiteLabelIntroScreen({
  introCopy,
  onOpenCamera,
  onFallback,
  fallbackAvailable,
  showPartnerFooter,
  children,
}: WhiteLabelIntroScreenProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.card}>
      <h1 className={styles.title}>
        {introCopy.headingLineKeys?.map((lineKey) => (
          <span key={lineKey}>{t(lineKey)}</span>
        )) ?? t(introCopy.headingKey)}
      </h1>

      <div className={[styles.docIllustration, styles.docIllustrationLanding].join(" ")}>
        <img src="/assets/legal-id-landing.png" alt={t('legalId.docIcon.alt')} />
      </div>

      <p className={styles.introSupportingText}>{t(introCopy.supportingTextKey)}</p>

      <p className={styles.consentText}>
        <Trans
          i18nKey="legalId.ready.consent"
          components={[
            <a
              key="policy-link"
              href="https://www.id.me/policies"
              target="_blank"
              rel="noopener noreferrer"
            />
          ]}
        />
      </p>

      <button className={styles.openCameraButton} onClick={onOpenCamera}>
        {t('legalId.ready.button')}
      </button>

      {children}

      {fallbackAvailable && (
        <button className={styles.noIdLink} onClick={onFallback}>
          {t(introCopy.fallbackTextKey ?? 'legalId.ready.fallback')}
        </button>
      )}

      {showPartnerFooter && <PartnerFooter />}
    </div>
  );
}
